import { h } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import PropTypes from 'prop-types'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { usePortal } from 'lib/portal'

import {
  disableDocumentScrolling,
  enableDocumentScrolling,
} from 'lib/modalHelpers'

import Modal from 'components/Modal'
import Header from 'components/Header'
import Form from 'components/Form'
import Checkbox from 'components/Checkbox'
import TextInput from 'components/TextInput'
// import TextArea from 'components/TextArea'
import Button from 'components/Button'
import ErrorMessage from 'components/ErrorMessage'
import Alert from 'components/Alert'
import SuccessAnimation from 'components/SuccessAnimation'
import './index.sass'
import classNames from 'lib/classNames'
import Icon from 'components/Icon'
import useSendHelpRequest from 'lib/sendHelpRequestHook'
import RichMediaInput from 'components/RichMediaInput'

const ANIMATION_DURATION = 300

export default function HelpAndFeedbackModal({
  open,
  onClose,
  className,
  scrollInternally = true,
}) {
  const renderInPortal = usePortal()
  const modalRef = useRef()
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [attachInfo, setAttachInfo] = useState(window.localStorage['HelpAndFeedback.attachInfo'] !== '0')


  const [localSentHelpRequest, setLocalSentHelpRequest] = useState(false)

  const {
    sendHelpRequest,
    sendingHelpRequest,
    errorSendingHelpRequest,
    sentHelpRequest,
  } = useSendHelpRequest()

  useEffect(() => {
    if (!open) {
      setMobile('')
      setEmail('')
      setMessage('')
      setAttachInfo(window.localStorage['HelpAndFeedback.attachInfo'] !== '0')

      setLocalSentHelpRequest(false)
    }
  }, [open])

  useEffect(() => {
    if (sentHelpRequest) {
      setLocalSentHelpRequest(true)
    }
  }, [sentHelpRequest])

  useEffect(() => {
    // const handleClickOutside = (event) => {
    //   if (open && modalRef.current && !modalRef.current.base.contains(event.target)) {
    //     console.log('handleClickOutside')
    //     event.preventDefault()
    //     onClose()
    //   }
    // }

    const handleFocusOutside = (event) => {
      console.log('handleFocusOutside')
      if (open && modalRef.current && !modalRef.current.base.contains(event.target)) {
        onClose()
      }
    }

    if (open) {
      disableDocumentScrolling()
      // document.addEventListener('click', handleClickOutside)
      document.addEventListener('focusout', handleFocusOutside)
    }

    return () => {
      enableDocumentScrolling()
      // document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('focusout', handleFocusOutside)
    }
  }, [open, onClose])


  useEffect(() => {
    if (sendingHelpRequest === false && sentHelpRequest) {
      setMobile('')
      setEmail('')
      setMessage('')
      setAttachInfo(window.localStorage['HelpAndFeedback.attachInfo'] !== '0')
      focusCloseButton()
    } else if (sendingHelpRequest === false && errorSendingHelpRequest) {
      focusTextarea()
    }
  }, [sendingHelpRequest, errorSendingHelpRequest, sentHelpRequest])

  const focusSelector = (selector) => {
    setTimeout(() => {
      const element = modalRef.current?.querySelector(selector)
      if (element) element.focus()
    }, 1)
  }

  const focusTextarea = () => focusSelector('textarea')
  const focusCloseButton = () => focusSelector('.HelpAndFeedbackModal-closeButton')

  const setField = (key) => (value) => {
    if (key === 'mobile') setMobile(value)
    if (key === 'email') setEmail(value)
    if (key === 'message') setMessage(value)
    if (key === 'attachInfo') {
      setAttachInfo(value)
      window.localStorage['HelpAndFeedback.attachInfo'] = value ? '1' : '0'
    }
  }

  const handleSubmit = () => {
    if (sendingHelpRequest) return
    sendHelpRequest({ email, mobile, message, attachInfo })
  }

  const submittable = message && message.length > 2

  const modalContent = open ? (
    <CSSTransition
      timeout={ANIMATION_DURATION}
      classNames="HelpAndFeedbackModal-animation"
      unmountOnExit
    >
      <Modal
        ref={modalRef}
        open={open}
        onClose={onClose}
        className={classNames('HelpAndFeedbackModal-window', { scrollInternally, className })}
        modalClassName="HelpAndFeedbackModal"
      >
        <Header size="lg" className="HelpAndFeedbackModal-header">
          <span className="HelpAndFeedbackModal-header-text">Help & Feedback</span>
          <Icon
            type={'cancel'}
            className="HelpAndFeedbackModal-closeButton"
            onClick={onClose}
          />
        </Header>
        <ModalBody>
          {localSentHelpRequest && !errorSendingHelpRequest ? (
            <Form>
              <Alert type="success" className="HelpAndFeedbackModal-successAlert">
                <p>Your message was sent!</p>
                <SuccessAnimation />
              </Alert>
              <Form.ButtonRow>
                <Button
                  autoFocus
                  type="primary"
                  onClick={onClose}
                  value="Close"
                />
              </Form.ButtonRow>
            </Form>
          ) : (
            <Form onSubmit={handleSubmit}>
              <ErrorMessage error={errorSendingHelpRequest} />
              <Form.Row>
                <Form.Label>Mobile Number</Form.Label>
                <TextInput
                  type="tel"
                  placeholder="Enter Mobile Number"
                  value={mobile}
                  onInput={setField('mobile')}
                  disabled={sendingHelpRequest}
                />
              </Form.Row>
              <Form.Row>
                <Form.Label>Email Address</Form.Label>
                <TextInput
                  type="email"
                  placeholder="Enter Email Address"
                  value={email}
                  onInput={setField('email')}
                  disabled={sendingHelpRequest}
                />
              </Form.Row>
              <Form.Row className="HelpAndFeedbackModal-messageRow">
                <Form.Label className="HelpAndFeedbackModal-messageLabel">How can we help?</Form.Label>
                <RichMediaInput {...{
                  className: 'HelpAndFeedbackModal-messageInput',
                  value: message,
                  onChange: setField('message'),
                  placeholder: 'Describe the issue which you were facing...',
                  disabled: sendingHelpRequest,
                }} />
              </Form.Row>
              <Form.Row>
                <span className="HelpAndFeedbackModal-attachInfo">
                  <Checkbox
                    checked={!!attachInfo}
                    onChange={setField('attachInfo')}
                    disabled={sendingHelpRequest}
                  />
                  {/* eslint-disable-next-line max-len */}
                  Attach information about the current state of this web page to our support staff to help better debug your issue.
                </span>
              </Form.Row>
              <Form.ButtonRow>
                <Button
                  className="HelpAndFeedbackModal-cancelButton"
                  type="subtle"
                  onClick={onClose}
                  value="cancel"
                  disabled={sendingHelpRequest}
                />
                <Button
                  type="primary"
                  submit
                  value={sendingHelpRequest ? 'Sending…' : 'Send'}
                  disabled={!submittable || sendingHelpRequest}
                />
              </Form.ButtonRow>
            </Form>
          )}
        </ModalBody>
      </Modal>
    </CSSTransition>
  ) : null

  return renderInPortal(
    <TransitionGroup component="span">{modalContent}</TransitionGroup>
  )
}

HelpAndFeedbackModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  className: PropTypes.string,
  scrollInternally: PropTypes.bool,
}

HelpAndFeedbackModal.defaultProps = {
  open: false,
  scrollInternally: true,
}

function ModalBody({ children }) {
  return <div className="HelpAndFeedbackModal-body">{children}</div>
}
